const ACTIVE_STATUSES = ['pending', 'generating']

// The API refuses an export that covers more products than this
const EXPORT_PRODUCT_LIMIT = 1000

function isExportActive (job) {
  return Boolean(job && ACTIVE_STATUSES.includes(job.status))
}

// Only a completed job can expire; failed jobs also carry is_expired once the stuck-job
// cleanup reaches them. The expiry time is checked too so a page left open stops offering
// the download once the file is gone, without waiting for the next refresh.
function isExportExpired (job, now = Date.now()) {
  if (!job || job.status !== 'complete') return false
  return Boolean(job.is_expired || (job.expires_at && new Date(job.expires_at).getTime() <= now))
}

function canRetryExport (job) {
  return Boolean(job && job.status === 'error')
}

function canAccessElabelExport (profile) {
  if (!profile || !profile.features || !profile.features.includes('elabel_export')) return false
  return Boolean(profile.is_staff || (profile.permissions?.includes('product_edit') && profile.plan?.interface?.['qrmanager:elabel']))
}

// The response carries no campaign or brand list, so the dialog writes the covered names
// into the export's description and the history reads them back. The description holds
// one block per kind, a "campaigns" or "brands" line followed by one name per line. The
// names are the ones at export time on purpose, so the row stays a record of what was
// exported. When they do not all fit the field, the list is cut and ends with a "+more" line.
// A "Select all" export stores a single "*all" line instead of names, because the backend
// then exports every product in the company, including archived campaigns and products
// without a campaign, so no list of names would be right.
const NAME_KINDS = ['campaigns', 'brands']
const DESCRIPTION_MAX_LENGTH = 500
const CUT_MARKER = '+more'
const ALL_MARKER = '*all'

// The names of one kind, ending with CUT_MARKER when the list was cut
function exportNames (job, kind) {
  if (!job || !job.description) return []
  const names = []
  let currentKind = null
  job.description.split('\n').forEach(line => {
    const text = line.trim()
    if (NAME_KINDS.includes(text)) currentKind = text
    else if (text && currentKind === kind) names.push(text)
  })
  return names
}

function exportCampaignNames (job) {
  return exportNames(job, 'campaigns')
}

function exportBrandNames (job) {
  return exportNames(job, 'brands')
}

function renderBlocks (blocks) {
  return blocks
    .filter(block => block.names.length)
    .map(block => [block.kind, ...block.names].join('\n'))
    .join('\n')
}

function isAllMarker (names) {
  return names.length === 1 && names[0] === ALL_MARKER
}

// Drops names from the end, brands before campaigns, until the description fits
function namesDescription ({ campaigns = [], brands = [], allCampaigns = false, allBrands = false }, maxLength = DESCRIPTION_MAX_LENGTH) {
  const blocks = [
    { kind: 'campaigns', names: allCampaigns ? [ALL_MARKER] : [...campaigns] },
    { kind: 'brands', names: allBrands ? [ALL_MARKER] : [...brands] }
  ]
  let description = renderBlocks(blocks)
  while (description.length > maxLength) {
    const block = blocks.slice().reverse().find(candidate => candidate.names.length > 1 || (candidate.names.length === 1 && candidate.names[0] !== CUT_MARKER))
    if (!block) break
    if (block.names[block.names.length - 1] === CUT_MARKER) block.names.splice(-2, 1)
    else block.names.splice(-1, 1, CUT_MARKER)
    description = renderBlocks(blocks)
  }
  return description
}

function apiErrorMessage (error, fallback) {
  const data = error && error.response && error.response.data
  if (!data) return fallback
  if (data.error_message || data.detail) return data.error_message || data.detail
  const fieldError = Object.values(data).find(value => Array.isArray(value) && value.length)
  return fieldError ? fieldError[0] : fallback
}

module.exports = {
  EXPORT_PRODUCT_LIMIT,
  isExportActive,
  isExportExpired,
  canRetryExport,
  canAccessElabelExport,
  CUT_MARKER,
  isAllMarker,
  exportCampaignNames,
  exportBrandNames,
  namesDescription,
  apiErrorMessage
}
